import { useState, useEffect, useRef } from 'react';
import {
  buildChartDomain,
  dataExtents,
  horizontalBarFromZero,
  valueToX,
  valueToY,
  verticalBarFromZero,
} from '../utils/barChartScale';
import { STATUS_COLORS } from '../config/colors';

interface BarChartData {
  label: string;
  values: { vehicle: string; value: number; color: string; emphasized?: boolean; stack?: string }[];
  /**
   * Dense charts (e.g. Orders 30d trend) hide mid-week x labels until hover
   * so the axis stays readable without losing the day on inspection.
   */
  hideAxisLabelUntilHover?: boolean;
  /**
   * Shorter text for the x axis only (e.g. "Tue" on a month view); hover,
   * tooltips and the hovered axis label keep the full `label`.
   */
  axisLabel?: string;
}

interface BarChartProps {
  data: BarChartData[];
  title?: string;
  height?: string;
  legendTitle?: string;
  /** Rotate x-axis category labels (degrees, clockwise). Extra bottom padding applied when non-zero. */
  xAxisLabelRotateDeg?: number;
  /**
   * When true, metrics are listed on the vertical axis and values extend horizontally
   * (swapped axes vs the default vertical bar layout).
   */
  horizontal?: boolean;
  /** Smaller padding and plot height for dashboard tiles (e.g. 2×2 grids). */
  compact?: boolean;
  /** Plot height in px, instead of the default for the layout (compact 152). Set it to fill a taller container. */
  plotHeight?: number;
  /** Share of each x slot a group of bars may use (default 0.6). */
  groupWidthFraction?: number;
  /** Widest a group of bars may be, in px (default 40). */
  maxGroupWidth?: number;
  /** Pack a group's side-by-side bars edge to edge (default: spread over n + 1 gaps, so each bar is thin). */
  fillGroup?: boolean;
  /** Span the whole container width at any zoom (default: at most about 80 px per group, centred). */
  fillWidth?: boolean;
  /** Thickest a single bar may be, in px; extra width becomes even spacing between groups. */
  maxBarWidth?: number;
  /** Font size, in px, of the value shown over a hovered bar (default 12). Setting it also reserves room above the top gridline, so a full-height bar's value is not drawn over the bar. */
  hoverValueFontSize?: number;
  /** Font size, in px, of the upright chart's axis labels (default: 12, or the brightAxisLabels sizes). */
  axisFontSize?: number;
  /** Omit the scenario/vehicle legend (e.g. when a parent shows one shared legend). */
  hideLegend?: boolean;
  /** Render without the chart card styling when embedded inside another panel. */
  embedded?: boolean;
  /** Stack positive/negative series in vertical bars instead of placing them side by side. */
  stacked?: boolean;
  /** Appended to y-axis ticks and bar hover values (e.g. `%` for completion rates). */
  yAxisSuffix?: string;
  /** Prefixed to y-axis ticks (e.g. `£` → `-£2000`). Currency symbols go before the magnitude. */
  yAxisPrefix?: string;
  /** Format y-axis magnitudes in thousands (e.g. `£15K`) so currency prefixes are not clipped. */
  formatYAxisAsK?: boolean;
  /** Hover border / shadow accent. Default green; Orders wallboard uses purple. */
  accent?: 'green' | 'purple';
  /** Brighter axis tick / category labels (e.g. Profit daily chart). */
  brightAxisLabels?: boolean;
  /**
   * Clip the plot to the chart box (Profit multi-line x labels).
   * Default false so hover guides on other charts are not clipped.
   */
  clipPlot?: boolean;
  /** Counts: whole-number y ticks only (a 0 to 1 range would otherwise tick at 0.2). */
  integerTicks?: boolean;
  /** Called with the group's index when one of its bars is clicked. */
  onGroupClick?: (groupIndex: number) => void;
}

const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export default function BarChart({
  data,
  title,
  height = 'h-80',
  legendTitle = 'Vehicle ID',
  xAxisLabelRotateDeg = 0,
  horizontal = false,
  compact = false,
  plotHeight,
  groupWidthFraction = 0.6,
  maxGroupWidth = 40,
  fillGroup = false,
  fillWidth = false,
  maxBarWidth,
  hoverValueFontSize: hoverValueFontSizeProp,
  axisFontSize,
  hideLegend = false,
  embedded = false,
  stacked = false,
  yAxisSuffix = '',
  yAxisPrefix = '',
  formatYAxisAsK = false,
  accent = 'green',
  brightAxisLabels = false,
  clipPlot = false,
  integerTicks = false,
  onGroupClick,
}: BarChartProps) {
  const accentHover =
    accent === 'purple'
      ? 'hover:border-brand hover:shadow-lg hover:shadow-brand/30'
      : 'hover:border-success hover:shadow-lg hover:shadow-success/30';
  const accentLabelHover = accent === 'purple' ? 'white' : STATUS_COLORS.success;
  const axisTickFill = brightAxisLabels ? 'rgb(var(--foreground))' : 'rgb(var(--muted-foreground))';
  const axisCategoryFill = brightAxisLabels ? 'rgb(var(--foreground))' : 'rgb(var(--muted-foreground))';
  const axisCategoryWeight = brightAxisLabels ? '600' : 'normal';
  const [hoveredBar, setHoveredBar] = useState<{
    groupIndex: number;
    vehicleIndex: number;
    screenX: number;
    screenY: number;
    value: number;
    color: string;
    barY: number;
    barCenterX: number;
    label: string;
    vehicleName: string;
  } | null>(null);
  const [hoveredLegendVehicle, setHoveredLegendVehicle] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(800);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const allValues = stacked && !horizontal
    ? data.flatMap((group) => [
        ...[...new Set(group.values.map((value) => value.stack ?? value.vehicle))].flatMap((stack) => [
          group.values
            .filter((value) => (value.stack ?? value.vehicle) === stack)
            .reduce((sum, value) => sum + Math.max(0, value.value), 0),
          group.values
            .filter((value) => (value.stack ?? value.vehicle) === stack)
            .reduce((sum, value) => sum + Math.min(0, value.value), 0),
        ]),
      ])
    : data.flatMap((d) => d.values.map((v) => v.value));
  const { minValue, maxValue } = dataExtents(allValues);
  const {
    ticks: yAxisTicks,
    displayMin,
    displayRange,
    showZeroBaseline,
  } = buildChartDomain(minValue, integerTicks ? Math.max(maxValue, 4) : maxValue, integerTicks ? 5 : 6);

  /** Taller plot + large bottom band when x labels rotate so text stays inside the SVG (avoids clipping). */
  const rotatedX = xAxisLabelRotateDeg !== 0;
  const multiLineX = data.some((d) => d.label.includes('\n'));
  const chartHeight = plotHeight ?? (compact ? (rotatedX ? 260 : multiLineX ? 220 : 152) : rotatedX ? 380 : multiLineX ? 360 : 320);
  const rightPadding = 20;
  const leftPadding = compact
    ? (showZeroBaseline || yAxisSuffix || yAxisPrefix ? 44 : 32)
    : (showZeroBaseline || yAxisSuffix || yAxisPrefix ? 52 : 40);
  // Always leave room for category labels (including single-bar Profit windows).
  const bottomPadding = rotatedX
    ? (compact ? 88 : 108)
    : multiLineX
      ? (compact ? 52 : 56)
      : 25;
  const hoverValueFontSize = hoverValueFontSizeProp ?? 12;
  const topPadding = hoverValueFontSizeProp !== undefined
    ? Math.max(compact ? 8 : 20, hoverValueFontSizeProp + 12)
    : compact ? 8 : 20;
  const zeroY = valueToY(0, displayMin, displayRange, chartHeight, topPadding, bottomPadding);
  const xLabelY = rotatedX
    ? chartHeight - Math.round(bottomPadding * 0.45)
    : multiLineX
      ? chartHeight - bottomPadding + 14
      : chartHeight - 10;

  const formatAxisValue = (n: number): string => {
    const magnitude = Math.abs(n);
    let base: string;
    if (formatYAxisAsK && magnitude >= 1000) {
      const valueInK = magnitude / 1000;
      base = valueInK % 1 === 0 || Math.abs(valueInK % 1) < 1e-9
        ? `${Math.round(valueInK)}K`
        : `${valueInK.toFixed(1)}K`;
    } else {
      base = Number.isInteger(magnitude) || Math.abs(magnitude % 1) < 1e-9
        ? String(Math.round(magnitude))
        : magnitude.toFixed(1);
    }
    // Currency prefixes sit after the sign: -£15K (not £-15K).
    if (yAxisPrefix) {
      const sign = n < 0 ? '-' : '';
      return `${sign}${yAxisPrefix}${base}${yAxisSuffix}`;
    }
    const signed = n < 0 ? `-${base}` : base;
    return yAxisSuffix ? `${signed}${yAxisSuffix}` : signed;
  };

  const availableWidth = containerWidth * 0.95;
  const minBarSpacing = Math.max(40, Math.min(80, availableWidth / data.length));
  const calculatedWidth = data.length * minBarSpacing + leftPadding + rightPadding;
  // fillWidth never exceeds the card: a narrow (phone) card must not scroll sideways.
  const chartWidth = fillWidth ? availableWidth : Math.min(availableWidth, Math.max(calculatedWidth, 300));

  const barWidth = Math.min(maxGroupWidth, ((chartWidth - leftPadding - rightPadding) / data.length) * groupWidthFraction);
  const groupSpacing = (chartWidth - leftPadding - rightPadding) / data.length;

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth || 800);
      }
    };

    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  type BarMetrics = {
    groupIndex: number;
    vehicleIndex: number;
    barCenterX: number;
    barY: number;
    value: number;
    color: string;
    label: string;
    vehicleName: string;
  };

  const calculateBarMetrics = (groupIndex: number, vehicleIndex: number): BarMetrics | null => {
    const group = data[groupIndex];
    if (!group) return null;
    const vehicle = group.values[vehicleIndex];
    if (!vehicle) return null;

    const groupCenter = leftPadding + groupIndex * groupSpacing + groupSpacing / 2;
    const vehicleCount = group.values.length || 1;
    // fillGroup: n bars edge to edge across the group; default: n bars over n + 1 gaps.
    const barSpacing = fillGroup ? barWidth / vehicleCount : barWidth / (vehicleCount + 1);
    const barX = fillGroup
      ? groupCenter - barWidth / 2 + (vehicleIndex + 0.5) * barSpacing
      : groupCenter - barWidth / 2 + (vehicleIndex + 1) * barSpacing;
    const rectWidth = Math.min(barSpacing * (fillGroup ? 0.88 : 0.8), maxBarWidth ?? Infinity);
    const rectX = fillGroup || maxBarWidth ? barX - rectWidth / 2 : barX - barSpacing / 2;
    const barCenterX = rectX + rectWidth / 2;
    const valueY = valueToY(vehicle.value, displayMin, displayRange, chartHeight, topPadding, bottomPadding);
    const bar = verticalBarFromZero(vehicle.value, zeroY, valueY);

    return {
      groupIndex,
      vehicleIndex,
      barCenterX,
      barY: bar.tip,
      value: vehicle.value,
      color: vehicle.color,
      label: group.label,
      vehicleName: vehicle.vehicle,
    };
  };

  const getScreenPosition = (svgX: number, svgY: number) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const svgRect = svg.getBoundingClientRect();
    const scaleX = svgRect.width / chartWidth;
    const scaleY = svgRect.height / chartHeight;
    return {
      x: svgRect.left + svgX * scaleX,
      y: svgRect.top + (svgY - 12) * scaleY,
    };
  };

  const setHoveredBarFromMetrics = (metrics: BarMetrics | null) => {
    if (!metrics) {
      setHoveredBar(null);
      return;
    }
    const screenPos = getScreenPosition(metrics.barCenterX, metrics.barY);
    if (!screenPos) return;
    setHoveredBar({
      ...metrics,
      screenX: screenPos.x,
      screenY: screenPos.y,
    });
  };

  const handleLabelHover = (groupIndex: number) => {
    const group = data[groupIndex];
    if (!group || group.values.length === 0) return;
    const preferredVehicleIndex = group.values.reduce((bestIdx, current, idx, arr) => {
      return current.value > arr[bestIdx].value ? idx : bestIdx;
    }, 0);
    setHoveredBarFromMetrics(calculateBarMetrics(groupIndex, preferredVehicleIndex));
  };

  const legendSeries = (() => {
    if (hideLegend || data.length === 0) return [];
    const byName = new Map<string, { vehicle: string; color: string }>();
    for (const group of data) {
      for (const v of group.values) {
        if (!byName.has(v.vehicle)) byName.set(v.vehicle, { vehicle: v.vehicle, color: v.color });
      }
    }
    return [...byName.values()];
  })();

  const legendBlock =
    !hideLegend && legendSeries.length > 0 ? (
      <div
        className={`absolute z-10 bg-background/90 px-3 py-2 rounded-lg border border-border ${
          compact ? 'top-3 right-3' : 'top-6 right-6'
        }`}
      >
        <div className="text-xs font-semibold text-foreground mb-2 pointer-events-none">{legendTitle}</div>
        <div className="flex flex-col gap-2">
          {legendSeries.map((vehicle, index) => {
            const isHoveredByBar = hoveredBar !== null && hoveredBar.vehicleName === vehicle.vehicle;
            const isHoveredByLegend = hoveredLegendVehicle === index;
            const isHovered = isHoveredByBar || isHoveredByLegend;
            const glowColor = hexToRgba(vehicle.color, 0.8);

            return (
              <div
                key={vehicle.vehicle}
                className="flex items-center gap-2 transition-all cursor-pointer"
                style={{
                  filter: isHovered ? `drop-shadow(0 0 8px ${glowColor})` : 'none',
                }}
                onMouseEnter={() => setHoveredLegendVehicle(index)}
                onMouseLeave={() => setHoveredLegendVehicle(null)}
              >
                <div
                  className={`w-3 h-3 rounded flex-shrink-0 transition-all ${isHovered ? 'scale-125' : ''}`}
                  style={{ backgroundColor: vehicle.color }}
                />
                <span
                  className={`text-xs whitespace-nowrap transition-all ${isHovered ? 'font-semibold' : 'text-foreground'}`}
                  style={{
                    color: isHovered ? vehicle.color : undefined,
                    textShadow: isHovered ? `0 0 6px ${glowColor}` : 'none',
                  }}
                >
                  {vehicle.vehicle}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    ) : null;

  /** Horizontal bars: category on Y, value on X. */
  if (horizontal && data.length > 0) {
    const marginL = 8;
    const labelRightW = 168;
    const marginR = 10;
    const hTopPad = 16;
    const hBottomPad = 34;
    const hSvgH = Math.max(268, data.length * 58 + hTopPad + hBottomPad);
    const hSvgW = Math.max(520, Math.min(containerWidth * 0.96, 960));
    const plotLeft = marginL;
    const plotTop = hTopPad;
    const plotW = hSvgW - marginL - labelRightW - marginR;
    const plotH = hSvgH - hTopPad - hBottomPad;
    const rowH = plotH / data.length;

    const hExtents = dataExtents(allValues);
    // Slight headroom for positive-only series (legacy padding).
    const hMaxForTicks =
      hExtents.maxValue > 0 && hExtents.minValue >= 0
        ? hExtents.maxValue * 1.06
        : hExtents.maxValue;
    const hDomain = buildChartDomain(hExtents.minValue, hMaxForTicks, 5);
    let hTicks = hDomain.ticks;
    while (hTicks.length >= 2 && hTicks[hTicks.length - 1]! > hExtents.maxValue * 1.18 && hExtents.maxValue > 0) {
      hTicks = hTicks.slice(0, -1);
    }
    if (hTicks.length < 2) {
      hTicks = buildChartDomain(hExtents.minValue, hMaxForTicks, 4).ticks;
    }
    const hDisplayMin = hTicks[0]!;
    const hDisplayMax = hTicks[hTicks.length - 1]!;
    const hDisplayRange = hDisplayMax - hDisplayMin || 1;
    const hShowZero = hDisplayMin < 0 && hDisplayMax >= 0;
    const zeroX = valueToX(0, hDisplayMin, hDisplayRange, plotLeft, plotW);

    const getScreenPositionH = (svgX: number, svgY: number) => {
      const svg = svgRef.current;
      if (!svg) return null;
      const svgRect = svg.getBoundingClientRect();
      const scaleX = svgRect.width / hSvgW;
      const scaleY = svgRect.height / hSvgH;
      return {
        x: svgRect.left + svgX * scaleX,
        y: svgRect.top + (svgY - 12) * scaleY,
      };
    };

    const setHoveredBarFromMetricsH = (m: BarMetrics | null) => {
      if (!m) {
        setHoveredBar(null);
        return;
      }
      const sp = getScreenPositionH(m.barCenterX, m.barY);
      if (!sp) return;
      setHoveredBar({ ...m, screenX: sp.x, screenY: sp.y });
    };

    const handleLabelHoverH = (groupIndex: number) => {
      const group = data[groupIndex];
      if (!group || group.values.length === 0) return;
      const preferredVehicleIndex = group.values.reduce((bestIdx, current, idx, arr) => {
        return current.value > arr[bestIdx].value ? idx : bestIdx;
      }, 0);
      const vehicle = group.values[preferredVehicleIndex];
      const vehicleCount = group.values.length || 1;
      const vh = rowH / (vehicleCount + 1);
      const rowTop = plotTop + groupIndex * rowH;
      const y = rowTop + (preferredVehicleIndex + 1) * vh - vh * 0.31;
      const barThick = vh * 0.62;
      const valueX = valueToX(vehicle.value, hDisplayMin, hDisplayRange, plotLeft, plotW);
      const bar = horizontalBarFromZero(vehicle.value, zeroX, valueX);
      const barCenterX = bar.tip;
      const barY = y + barThick / 2;
      setHoveredBarFromMetricsH({
        groupIndex,
        vehicleIndex: preferredVehicleIndex,
        barCenterX,
        barY,
        value: vehicle.value,
        color: vehicle.color,
        label: group.label,
        vehicleName: vehicle.vehicle,
      });
    };

    return (
      <div
        className={`${embedded ? '' : `bg-card rounded-lg shadow-sm border border-border ${accentHover} transition-all`} relative flex flex-col w-full ${
          compact ? (embedded ? 'min-h-0' : 'min-h-0 p-2.5') : embedded ? '' : 'min-h-100 p-6'
        } ${height}`}
      >
        {title && (
          <h3 className={`font-semibold text-white ${compact ? 'text-sm mb-1' : 'text-lg mb-4'}`}>{title}</h3>
        )}
        {legendBlock}

        <div
          ref={containerRef}
          className="flex-1 min-h-0 w-full relative flex items-center justify-center"
          style={{ overflow: 'visible', minHeight: hSvgH }}
        >
          <svg
            ref={svgRef}
            width={hSvgW}
            height={hSvgH}
            overflow="visible"
            style={{ display: 'block', position: 'relative', zIndex: 1, overflow: 'visible' }}
          >
            {hTicks.map((tickValue, index) => {
              const gx = valueToX(tickValue, hDisplayMin, hDisplayRange, plotLeft, plotW);
              return (
                <g key={`hgrid-${index}`}>
                  <line
                    x1={gx}
                    y1={plotTop}
                    x2={gx}
                    y2={hSvgH - hBottomPad}
                    stroke="rgb(var(--secondary))"
                    strokeWidth={1}
                    strokeDasharray="2,2"
                  />
                  <text
                    x={gx}
                    y={hSvgH - 10}
                    fill="rgb(var(--muted-foreground))"
                    fontSize="11"
                    textAnchor="middle"
                  >
                    {formatAxisValue(tickValue)}
                  </text>
                </g>
              );
            })}

            {hShowZero && (
              <line
                x1={zeroX}
                y1={plotTop}
                x2={zeroX}
                y2={hSvgH - hBottomPad}
                stroke="rgb(var(--muted-foreground))"
                strokeWidth={1.5}
                opacity={0.85}
              />
            )}

            {data.map((group, groupIndex) => {
              const rowTop = plotTop + groupIndex * rowH;
              const vehicleCount = group.values.length || 1;
              const vh = rowH / (vehicleCount + 1);
              const labelHovered = hoveredBar?.groupIndex === groupIndex;
              const showAxisLabel = Boolean(group.label)
                && (!group.hideAxisLabelUntilHover || labelHovered);
              return (
                <g key={`row-${groupIndex}`}>
                  {group.values.map((vehicle, vehicleIndex) => {
                    const y = rowTop + (vehicleIndex + 1) * vh - vh * 0.31;
                    const barThick = vh * 0.62;
                    const valueX = valueToX(vehicle.value, hDisplayMin, hDisplayRange, plotLeft, plotW);
                    const bar = horizontalBarFromZero(vehicle.value, zeroX, valueX);
                    const isHoveredByBar =
                      hoveredBar !== null &&
                      hoveredBar.groupIndex === groupIndex &&
                      hoveredBar.vehicleIndex === vehicleIndex;
                    const isHoveredByLegend = hoveredLegendVehicle === vehicleIndex;
                    const isHovered = isHoveredByBar || isHoveredByLegend;
                    const shouldDim = hoveredLegendVehicle !== null && hoveredLegendVehicle !== vehicleIndex;
                    const glowColor = hexToRgba(vehicle.color, 0.8);
                    const formattedValue = formatAxisValue(vehicle.value);
                    const barCenterX = bar.tip;
                    const barCy = y + barThick / 2;
                    const labelX =
                      vehicle.value >= 0
                        ? bar.tip + 6
                        : bar.tip - 6;

                    return (
                      <g key={`${groupIndex}-${vehicleIndex}`}>
                        <rect
                          x={bar.origin}
                          y={y}
                          width={bar.size}
                          height={barThick}
                          rx={2}
                          fill={vehicle.color}
                          className="transition-all cursor-pointer"
                          style={{
                            opacity: shouldDim ? 0.3 : hoveredBar === null || isHovered ? 1 : 0.5,
                            filter: isHovered ? `brightness(1.3) drop-shadow(0 0 8px ${glowColor})` : 'none',
                          }}
                          onMouseEnter={() => {
                            setHoveredBarFromMetricsH({
                              groupIndex,
                              vehicleIndex,
                              barCenterX,
                              barY: barCy,
                              value: vehicle.value,
                              color: vehicle.color,
                              label: group.label,
                              vehicleName: vehicle.vehicle,
                            });
                          }}
                          onMouseLeave={() => setHoveredBar(null)}
                        />
                        {isHovered && (
                          <text
                            x={labelX}
                            y={barCy}
                            dominantBaseline="middle"
                            textAnchor={vehicle.value >= 0 ? 'start' : 'end'}
                            fill="white"
                            fontSize="11"
                            fontWeight="bold"
                            className="pointer-events-none drop-shadow-glow-neutral-md"
                          >
                            {formattedValue}
                          </text>
                        )}
                      </g>
                    );
                  })}
                  {showAxisLabel && (
                  <text
                    x={plotLeft + plotW + 8}
                    y={rowTop + rowH / 2}
                    fill={labelHovered ? STATUS_COLORS.success : 'rgb(var(--muted-foreground))'}
                    fontSize="11"
                    fontWeight={labelHovered ? 'bold' : 'normal'}
                    dominantBaseline="middle"
                    textAnchor="start"
                    className="cursor-pointer transition-all"
                    onMouseEnter={() => handleLabelHoverH(groupIndex)}
                    onMouseLeave={() => setHoveredBar(null)}
                  >
                    {group.label.length > 26 ? `${group.label.slice(0, 24)}…` : group.label}
                  </text>
                  )}
                </g>
              );
            })}

            {hoveredBar && (
              <line
                x1={hoveredBar.value >= 0 ? zeroX : hoveredBar.barCenterX}
                y1={hoveredBar.barY}
                x2={hoveredBar.value >= 0 ? hoveredBar.barCenterX : zeroX}
                y2={hoveredBar.barY}
                stroke="white"
                strokeWidth={1.5}
                strokeDasharray="4,4"
                opacity={0.6}
                className="pointer-events-none"
              />
            )}
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${embedded ? '' : `bg-card rounded-lg shadow-sm border border-border ${accentHover} transition-all`} relative flex flex-col w-full ${
        clipPlot ? 'overflow-hidden' : ''
      } ${compact ? (embedded ? 'min-h-0' : 'min-h-0 p-2.5') : embedded ? '' : 'min-h-100 p-6'} ${height}`}
    >
      {title && (
        <h3 className={`font-semibold text-white ${compact ? 'text-sm mb-1' : 'text-lg mb-4'}`}>{title}</h3>
      )}
      {legendBlock}

      <div
        ref={containerRef}
        className={`flex-1 min-h-0 w-full relative flex items-center justify-center ${clipPlot ? 'overflow-hidden' : ''}`}
        style={{
          overflow: clipPlot ? undefined : 'visible',
          ...(rotatedX || multiLineX ? { minHeight: chartHeight } : {}),
        }}
      >
        <svg
          ref={svgRef}
          width={chartWidth}
          height={chartHeight}
          overflow={clipPlot ? 'hidden' : 'visible'}
          style={{
            display: 'block',
            position: 'relative',
            zIndex: 1,
            overflow: clipPlot ? undefined : 'visible',
          }}
        >
          {yAxisTicks.map((tickValue, index) => {
            const y = valueToY(tickValue, displayMin, displayRange, chartHeight, topPadding, bottomPadding);
            return (
              <g key={index}>
                <line
                  x1={leftPadding}
                  y1={y}
                  x2={chartWidth - rightPadding}
                  y2={y}
                  stroke="rgb(var(--secondary))"
                  strokeWidth={1}
                  strokeDasharray="2,2"
                />
                <text
                  x={leftPadding - 8}
                  y={y + 4}
                  fill={axisTickFill}
                  fontSize={axisFontSize ?? (brightAxisLabels ? '12.5' : '12')}
                  fontWeight={brightAxisLabels ? '600' : 'normal'}
                  textAnchor="end"
                >
                  {formatAxisValue(tickValue)}
                </text>
              </g>
            );
          })}

          {showZeroBaseline && (
            <line
              x1={leftPadding}
              y1={zeroY}
              x2={chartWidth - rightPadding}
              y2={zeroY}
              stroke="rgb(var(--muted-foreground))"
              strokeWidth={1.5}
              opacity={0.85}
            />
          )}

          {(() => {
            const hasEmphasized = data.some((g) => g.values.some((v) => v.emphasized));
            return data.map((group, groupIndex) => {
            const x = leftPadding + groupIndex * groupSpacing + groupSpacing / 2;
            const stackKeys = [...new Set(group.values.map((value) => value.stack ?? value.vehicle))];
            const stackIndex = new Map(stackKeys.map((key, index) => [key, index]));
            const vehicleCount = group.values.length;
            const packed = fillGroup && !stacked && vehicleCount > 1;
            const barSpacing = packed ? barWidth / vehicleCount : barWidth / (stackKeys.length + 1);
            const positiveStacks = new Map<string, number>();
            const negativeStacks = new Map<string, number>();

            return group.values.map((vehicle, vehicleIndex) => {
              const stackKey = vehicle.stack ?? vehicle.vehicle;
              const positiveStack = positiveStacks.get(stackKey) ?? 0;
              const negativeStack = negativeStacks.get(stackKey) ?? 0;
              const stackBase = vehicle.value >= 0 ? positiveStack : negativeStack;
              const stackEnd = stackBase + vehicle.value;
              if (vehicle.value >= 0) positiveStacks.set(stackKey, stackEnd);
              else negativeStacks.set(stackKey, stackEnd);
              const barX = stacked
                ? x - barWidth / 2 + ((stackIndex.get(stackKey) ?? 0) + 1) * barSpacing
                : packed
                  ? x - barWidth / 2 + (vehicleIndex + 0.5) * barSpacing
                  : x - barWidth / 2 + (vehicleIndex + 1) * barSpacing;
              const valueY = valueToY(stacked ? stackEnd : vehicle.value, displayMin, displayRange, chartHeight, topPadding, bottomPadding);
              const barBaseY = stacked
                ? valueToY(stackBase, displayMin, displayRange, chartHeight, topPadding, bottomPadding)
                : zeroY;
              const bar = verticalBarFromZero(vehicle.value, barBaseY, valueY);
              // A bar is never thicker than maxBarWidth; it stays centred on its spot.
              const rectWidth = Math.min(
                stacked
                  ? barSpacing * 0.8
                  : vehicleCount === 1 ? barWidth * 0.85 : packed ? barSpacing * 0.88 : barSpacing * 0.8,
                maxBarWidth ?? Infinity,
              );
              const rectX = stacked
                ? barX - rectWidth / 2
                : vehicleCount === 1 || packed || maxBarWidth ? barX - rectWidth / 2 : barX - barSpacing / 2;
              const barCenterX = rectX + rectWidth / 2;
              const isHoveredByBar =
                hoveredBar !== null &&
                hoveredBar.groupIndex === groupIndex &&
                hoveredBar.vehicleIndex === vehicleIndex;
              const isHoveredByLegend = hoveredLegendVehicle === vehicleIndex;
              const isHovered = isHoveredByBar || isHoveredByLegend;
              const shouldDim = hoveredLegendVehicle !== null && hoveredLegendVehicle !== vehicleIndex;
              const glowColor = hexToRgba(vehicle.color, 0.8);
              const formattedValue = formatAxisValue(vehicle.value);
              const labelY =
                vehicle.value >= 0
                  ? Math.max(bar.tip - 8, hoverValueFontSize)
                  : Math.min(bar.tip + 14, chartHeight - Math.max(bottomPadding - 4, 4));
              const baseOpacity = shouldDim
                ? 0.3
                : vehicle.emphasized
                  ? 1
                  : hasEmphasized
                    ? 0.55
                    : hoveredBar === null || isHovered
                      ? 1
                      : 0.5;
              const baseFilter = vehicle.emphasized
                ? `brightness(1.12) drop-shadow(0 0 6px ${glowColor})`
                : 'none';

              return (
                <g key={`${groupIndex}-${vehicleIndex}`}>
                  <rect
                    x={rectX}
                    y={bar.origin}
                    width={rectWidth}
                    height={bar.size}
                    fill={vehicle.color}
                    stroke={vehicle.emphasized ? STATUS_COLORS.warning : undefined}
                    strokeWidth={vehicle.emphasized ? 1.5 : undefined}
                    role={onGroupClick ? 'button' : undefined}
                    tabIndex={onGroupClick ? 0 : undefined}
                    aria-label={onGroupClick
                      ? `Open details for ${group.label}: ${vehicle.vehicle}, ${formattedValue}`
                      : undefined}
                    className="transition-all cursor-pointer"
                    style={{
                      opacity: baseOpacity,
                      filter: isHovered
                        ? `brightness(1.3) drop-shadow(0 0 8px ${glowColor})`
                        : baseFilter,
                    }}
                    onMouseEnter={() => {
                      setHoveredBarFromMetrics({
                        groupIndex,
                        vehicleIndex,
                        barCenterX,
                        barY: bar.tip,
                        value: vehicle.value,
                        color: vehicle.color,
                        label: group.label,
                        vehicleName: vehicle.vehicle,
                      });
                    }}
                    onMouseLeave={() => setHoveredBar(null)}
                    onClick={onGroupClick ? () => onGroupClick(groupIndex) : undefined}
                    onKeyDown={onGroupClick ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onGroupClick(groupIndex);
                      }
                    } : undefined}
                  />
                  {isHovered && (
                    <text
                      x={barCenterX}
                      y={labelY}
                      fontSize={hoverValueFontSize}
                      fontWeight="bold"
                      style={{ fill: 'rgb(var(--foreground))' }}
                      textAnchor="middle"
                      className="pointer-events-none drop-shadow-glow-neutral-md"
                    >
                      {formattedValue}
                    </text>
                  )}
                </g>
              );
            });
            });
          })()}

          {hoveredBar && (
            <line
              x1={leftPadding}
              y1={hoveredBar.barY}
              x2={hoveredBar.barCenterX}
              y2={hoveredBar.barY}
              stroke="white"
              strokeWidth={1.5}
              strokeDasharray="4,4"
              opacity={0.6}
              className="pointer-events-none"
            />
          )}

          {data.map((group, groupIndex) => {
              if (!group.label) return null;
              const x = leftPadding + groupIndex * groupSpacing + groupSpacing / 2;
              const isHovered = hoveredBar?.groupIndex === groupIndex;
              if (group.hideAxisLabelUntilHover && !isHovered) return null;
              const labelLines = (isHovered ? group.label : group.axisLabel ?? group.label).split('\n');
              return (
                <text
                  key={groupIndex}
                  x={x}
                  y={xLabelY}
                  fill={isHovered ? accentLabelHover : axisCategoryFill}
                  fontSize={axisFontSize ?? (brightAxisLabels ? '11.5' : '12')}
                  fontWeight={isHovered ? 'bold' : axisCategoryWeight}
                  textAnchor="middle"
                  className="cursor-pointer transition-all"
                  style={{
                    filter: isHovered ? `drop-shadow(0 0 6px ${accentLabelHover})` : 'none',
                  }}
                  transform={
                    xAxisLabelRotateDeg !== 0
                      ? `rotate(${xAxisLabelRotateDeg}, ${x}, ${xLabelY})`
                      : undefined
                  }
                  onMouseEnter={() => handleLabelHover(groupIndex)}
                  onMouseLeave={() => setHoveredBar(null)}
                >
                  {labelLines.map((line, lineIndex) => (
                    <tspan key={lineIndex} x={x} dy={lineIndex === 0 ? 0 : 14}>
                      {line}
                    </tspan>
                  ))}
                </text>
              );
            })}
        </svg>
      </div>
    </div>
  );
}
